import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePlayers } from '../../hooks/usePlayers';
import { useUser } from '../../hooks/useUser';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { InlineError } from '../ui/ErrorDisplay';
import type { Player } from '../../types';

export function PlayerSelector() {
  const { players, loading, error, retryLastOperation } = usePlayers();
  const { selectedPlayer, setSelectedPlayer } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredPlayers = players.filter(player =>
    player.bowler_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    player.team_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePlayerSelect = async (player: Player) => {
    await setSelectedPlayer(player);
    setIsOpen(false);
    setSearchTerm('');
  };

  if (!selectedPlayer) {
    return (
      <Link
        to="/select-player"
        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
      >
        Select Player
      </Link>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <span className="truncate max-w-32 sm:max-w-48">
          {selectedPlayer.bowler_name}
        </span>
        <svg
          className={`ml-2 h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-[60]">
          <div className="p-3 border-b border-gray-200">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search players..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
              autoFocus
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {error ? (
              <div className="p-3">
                <InlineError 
                  error={error} 
                  onRetry={retryLastOperation}
                  className="text-xs"
                />
              </div>
            ) : loading ? (
              <div className="p-4 text-center text-gray-500">
                <LoadingSpinner size="sm" className="mx-auto mb-2" />
                <span className="text-sm">Loading players...</span>
              </div>
            ) : filteredPlayers.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <span className="text-sm">{searchTerm ? 'No players found' : 'No players available'}</span>
              </div>
            ) : (
              <div className="py-1">
                {filteredPlayers.slice(0, 10).map((player) => (
                  <button
                    key={player.bowler_id}
                    onClick={() => handlePlayerSelect(player)}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-100 focus:outline-none focus:bg-gray-100 touch-manipulation transition-colors ${
                      selectedPlayer.bowler_id === player.bowler_id
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-900'
                    }`}
                  >
                    <div className="font-medium truncate">{player.bowler_name}</div>
                    <div className="text-xs text-gray-500 truncate">{player.team_name}</div>
                  </button>
                ))}
                
                {filteredPlayers.length > 10 && (
                  <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-200">
                    Showing first 10 of {filteredPlayers.length} results
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-gray-200">
            <Link
              to="/select-player"
              onClick={() => setIsOpen(false)}
              className="block w-full text-center px-4 py-3 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors touch-manipulation"
            >
              View All Players
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}